import { ConfigError } from '../types';

export interface ScreenRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ScreenConfig {
  pixelX: number;
  pixelY: number;
  displayCols: number;
  rows: number;
  pixelSize: number;
  rect: ScreenRect;
}

export interface KeyMapEntry {
  keyCode: string;
  scanCode: number;
  row?: number;
  col?: number;
}

export interface ButtonDef {
  x: number;
  y: number;
  w: number;
  h: number;
  keyCode: string;
  label: string;
}

export type SegmentType = 'ROM' | 'RAM' | 'PERIPHERAL' | 'UNMAPPED' | 'MIRROR';

export interface SegmentDef {
  segment: number;
  type: SegmentType;
  name?: string;
  size?: number;
  mirrorSource?: number;
}

export interface PeripheralDef {
  type: string;
  baseAddress: number;
  enabled: boolean;
  options?: Record<string, unknown>;
}

export interface IconPosition {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ModelManifestEntry {
  id: string;
  name: string;
  hardwareGeneration: string;
  romPath: string;
  hasInterfaceImage: boolean;
}

export interface InkColor {
  r: number;
  g: number;
  b: number;
}

export interface ModelConfig {
  modelName: string;
  hardwareId: number;
  hardwareGeneration: string;
  csrMask: number;
  realHardware: boolean;
  pdValue: number;

  romData?: Uint8Array;
  interfaceImage?: ImageData;

  screen: ScreenConfig;
  keyMap: KeyMapEntry[];
  buttons: ButtonDef[];
  segments: SegmentDef[];
  peripherals: PeripheralDef[];
  statusIcons: Record<string, IconPosition>;

  inkColor: InkColor;
  interfaceImagePath?: string;
}

const VALID_HARDWARE_IDS = [3, 4, 5] as const;
const VALID_GENERATIONS = ['ES_PLUS', 'CLASSWIZ', 'CLASSWIZ_II'] as const;

export function validateModelConfig(config: unknown): ModelConfig {
  if (!config || typeof config !== 'object') {
    throw new ConfigError('Model config must be a non-null object');
  }

  const c = config as Record<string, unknown>;

  if (typeof c.modelName !== 'string' || c.modelName.length === 0) {
    throw new ConfigError('Missing or invalid required field: modelName (must be non-empty string)');
  }

  if (typeof c.hardwareId !== 'number' || !(VALID_HARDWARE_IDS as readonly number[]).includes(c.hardwareId)) {
    throw new ConfigError(`Invalid hardwareId: ${c.hardwareId}. Must be one of: ${VALID_HARDWARE_IDS.join(', ')}`);
  }

  if (typeof c.hardwareGeneration !== 'string' || !(VALID_GENERATIONS as readonly string[]).includes(c.hardwareGeneration)) {
    throw new ConfigError(`Invalid hardwareGeneration: ${c.hardwareGeneration}. Must be one of: ${VALID_GENERATIONS.join(', ')}`);
  }

  if (typeof c.csrMask !== 'number') {
    throw new ConfigError('Missing or invalid required field: csrMask (must be a number)');
  }

  if (typeof c.screen !== 'object' || c.screen === null) {
    throw new ConfigError('Missing or invalid required field: screen (must be an object)');
  }

  if (!Array.isArray(c.keyMap)) {
    throw new ConfigError('Missing or invalid required field: keyMap (must be an array)');
  }

  if (!Array.isArray(c.buttons)) {
    throw new ConfigError('Missing or invalid required field: buttons (must be an array)');
  }

  if (!Array.isArray(c.segments) || c.segments.length === 0) {
    throw new ConfigError('Missing or invalid required field: segments (must be a non-empty array)');
  }

  if (!Array.isArray(c.peripherals)) {
    throw new ConfigError('Missing or invalid required field: peripherals (must be an array)');
  }

  if (typeof c.statusIcons !== 'object' || c.statusIcons === null) {
    throw new ConfigError('Missing or invalid required field: statusIcons (must be an object)');
  }

  if (typeof c.inkColor !== 'object' || c.inkColor === null) {
    throw new ConfigError('Missing or invalid required field: inkColor (must be an object)');
  }

  const ink = c.inkColor as Record<string, unknown>;
  if (typeof ink.r !== 'number' || typeof ink.g !== 'number' || typeof ink.b !== 'number') {
    throw new ConfigError('inkColor must have r, g, b numeric fields');
  }

  return config as ModelConfig;
}
